import { CommandPaletteTrigger } from "@/components/command-palette";
import { ConfidenceIndicator } from "@/components/confidence-indicator";
import { OctaLogo } from "@/components/octa-logo";
import { QuickTaskDialog } from "@/components/quick-task-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useEntitySwitch } from "@/hooks/use-entity-switch";
import { useToast } from "@/hooks/use-toast";
import { useAuth, useCanWrite } from "@/lib/auth";
import { isOwnBulkEvent } from "@/lib/bulk-event-recipient";
import { getFailureRecencyUrgency, getNotificationBadgeUrgency, URGENCY_CLASSES } from "@/lib/color-intelligence";
import { useActiveEntity } from "@/lib/entity-context";
import { isFinancingAvailable, useFeatures } from "@/lib/features";
import { type NavItem, PRIMARY_NAV_ITEMS } from "@/lib/navigation-registry";
import { notificationHref } from "@/lib/notification-href";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { prefetchRoute } from "@/lib/route-prefetch";
import { getSSEClient, setSSEReconnectedHandler, setSSEReconnectExhaustedHandler } from "@/lib/sseClient";
import { useSupportMode } from "@/lib/support-mode";
import { useFeatureAwareness } from "@/lib/use-feature-awareness";
import { parsePushFailureMessage } from "@shared/lib/parse-push-failure";
import type { Notification } from "@shared/schema";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  ActivitySquare,
  AlertTriangle,
  BarChart3,
  Bell,
  Building2,
  Check,
  CheckCheck,
  CheckCircle,
  ChevronDown,
  ClipboardList,
  Clock,
  CreditCard,
  FileText,
  Inbox as InboxIcon,
  Layers,
  Link2,
  ListTodo,
  LogOut,
  Mail,
  MessageSquare,
  MoreHorizontal,
  Plus,
  RefreshCw,
  Search,
  Settings,
  Shield,
  ShieldCheck,
  XCircle,
  Zap,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "wouter";

function formatRelativeTime(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffMs = now - then;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
}

function usePaymentSound() {
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const play = useCallback(() => {
    const soundEnabled = localStorage.getItem("octa_payment_sound") !== "off";
    if (!soundEnabled) return;

    if (!audioRef.current) {
      audioRef.current = new Audio("/kaching.wav");
    }
    audioRef.current.currentTime = 0;
    audioRef.current.volume = 0.3;
    audioRef.current.play().catch((err: unknown) => {
      console.warn(
        "[header-nav] Audio play blocked (browser policy):",
        err instanceof Error ? err.message : String(err),
      );
    });
  }, []);

  return { play };
}

function NotificationBell() {
  const [, setLocation] = useLocation();
  const { t } = useTranslation("common");
  const { t: tAp } = useTranslation("ap");
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const { play: playPaymentSound } = usePaymentSound();
  const [snoozeMenuId, setSnoozeMenuId] = useState<string | null>(null);
  const [taskDialogNotification, setTaskDialogNotification] = useState<Notification | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const { activeEntityId, isAllEntities } = useActiveEntity();
  // OCT-16164: read inside the SSE handlers below through a ref, so the
  // subscription effect does not tear down and reconnect when auth loads.
  const { user } = useAuth();
  const canWrite = useCanWrite();
  const currentUserIdRef = useRef<string | null>(user?.id ?? null);
  currentUserIdRef.current = user?.id ?? null;

  const notificationEntityParam = isAllEntities ? "" : activeEntityId ? `?entityId=${activeEntityId}` : "";
  const { data: notificationsData } = useQuery<{ data: Notification[]; total: number; unreadCount: number; }>({
    queryKey: ["/api/notifications", activeEntityId],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/notifications${notificationEntityParam}`);
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
    refetchInterval: 30000,
  });

  useEffect(() => {
    let dismissConnectionToast: (() => void) | undefined;
    setSSEReconnectExhaustedHandler(() => {
      if (dismissConnectionToast) return;
      const { dismiss } = toast({
        title: "Connection lost",
        description: "Real-time updates are temporarily unavailable. Reconnecting…",
        variant: "destructive",
        duration: 0,
      });
      dismissConnectionToast = dismiss;
    });
    setSSEReconnectedHandler(() => {
      dismissConnectionToast?.();
      dismissConnectionToast = undefined;
    });

    const sse = getSSEClient();

    // OCT-10779: the wildcard handler below fires on EVERY domain event with
    // no debounce — a bulk import creating N invoices previously triggered N
    // synchronous `/api/notifications` refetches on top of the independent
    // 30s poll above, which was enough on its own to help exhaust the
    // platform's per-user rate-limit bucket mid-import. Coalesce a burst of
    // events into one refetch a few seconds after the last event instead of
    // one refetch per event.
    let wildcardInvalidateTimer: ReturnType<typeof setTimeout> | undefined;

    const unsubPayment = sse.on("payment_received", (data) => {
      const d = data as { payload?: Record<string, unknown>; } & Record<string, unknown>;
      const payload = (d.payload || d) as Record<string, unknown>;
      const amount = (payload.amount as string) || "";
      const currency = (payload.currency as string) || "";
      const customerName = (payload.customerName as string) || "";
      const invoiceNumber = (payload.invoiceNumber as string) || "";

      toast({
        title: t("sse.paymentReceived"),
        description: t("sse.paymentDescription", { currency, amount, customerName, invoiceNumber }),
        duration: 6000,
      });

      playPaymentSound();

      queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
      queryClient.invalidateQueries({ queryKey: ["/api/invoices"] });
    });

    const unsubDisputeOpened = sse.on("dispute_opened", (data) => {
      const d = data as { payload?: Record<string, unknown>; } & Record<string, unknown>;
      const payload = (d.payload || d) as Record<string, unknown>;
      const customerName = (payload.customerName as string) || "";
      const invoiceNumber = (payload.invoiceNumber as string) || "";

      toast({
        title: "New Dispute Opened",
        description: `${customerName} disputed invoice #${invoiceNumber}`,
        variant: "destructive",
        duration: 8000,
      });

      queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
      queryClient.invalidateQueries({ queryKey: ["/api/invoices"] });
    });

    const unsubDisputeResolved = sse.on("dispute_resolved", () => {
      queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
      queryClient.invalidateQueries({ queryKey: ["/api/invoices"] });
    });

    const unsubDisputeStatusChanged = sse.on("dispute_status_changed", () => {
      queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
    });

    // OCT-10779: bookend notifications for a CSV bulk invoice import — one
    // toast when it starts, one with the created/failed summary when it
    // finishes, instead of the per-row notification churn a large import
    // would otherwise generate.
    const unsubBulkImportStarted = sse.on("invoice_bulk_import_started", (data) => {
      const d = data as { payload?: Record<string, unknown>; } & Record<string, unknown>;
      const payload = (d.payload || d) as Record<string, unknown>;
      const requested = (payload.requested as number) ?? 0;

      toast({
        title: "CSV import started",
        description: `Importing ${requested} invoice${requested === 1 ? "" : "s"} from CSV…`,
        duration: 5000,
      });

      queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
    });

    const unsubBulkImportCompleted = sse.on("invoice_bulk_import_completed", (data) => {
      const d = data as { payload?: Record<string, unknown>; } & Record<string, unknown>;
      const payload = (d.payload || d) as Record<string, unknown>;
      const created = (payload.created as number) ?? 0;
      const failed = (payload.failed as number) ?? 0;
      const requested = (payload.requested as number) ?? created + failed;

      toast({
        title: "CSV import complete",
        description: failed === 0
          ? `${created} invoice${created === 1 ? "" : "s"} imported from CSV.`
          : `${created} of ${requested} invoices imported from CSV (${failed} failed).`,
        variant: failed === 0 ? "default" : "destructive",
        duration: 8000,
      });

      queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
      queryClient.invalidateQueries({ queryKey: ["/api/invoices"] });
    });

    // OCT-10809: bookend notifications for a bulk invoice approve — same
    // shape as the CSV bulk-import bookends above, now that bulk approve
    // runs as one server-side batch request instead of N client-fired ones.
    const unsubBulkApproveStarted = sse.on("invoice_bulk_approve_started", (data) => {
      const d = data as { payload?: Record<string, unknown>; } & Record<string, unknown>;
      const payload = (d.payload || d) as Record<string, unknown>;
      // OCT-16164: only the user who ran the batch gets its toast; every
      // client still refreshes its queries below.
      const isOwnBatch = isOwnBulkEvent(payload, currentUserIdRef.current);
      const requested = (payload.requested as number) ?? 0;

      if (isOwnBatch) {
        toast({
          title: "Bulk approve started",
          description: `Approving ${requested} invoice${requested === 1 ? "" : "s"}…`,
          duration: 5000,
        });
      }

      queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
    });

    const unsubBulkApproveCompleted = sse.on("invoice_bulk_approve_completed", (data) => {
      const d = data as { payload?: Record<string, unknown>; } & Record<string, unknown>;
      const payload = (d.payload || d) as Record<string, unknown>;
      // OCT-16164: only the user who ran the batch gets its toast; every
      // client still refreshes its queries below.
      const isOwnBatch = isOwnBulkEvent(payload, currentUserIdRef.current);
      const approved = (payload.approved as number) ?? 0;
      const failed = (payload.failed as number) ?? 0;
      const requested = (payload.requested as number) ?? approved + failed;

      if (isOwnBatch) {
        toast({
          title: "Bulk approve complete",
          description: failed === 0
            ? `${approved} invoice${approved === 1 ? "" : "s"} approved.`
            : `${approved} of ${requested} invoices approved (${failed} failed).`,
          variant: failed === 0 ? "default" : "destructive",
          duration: 8000,
        });
      }

      queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
      queryClient.invalidateQueries({ queryKey: ["/api/invoices"] });
    });

    // OCT-10809 follow-up: "Bulk approve complete" above only ever reflects
    // LOCAL approval — the provider push fan-out hasn't run yet when that
    // notification fires. This separate event reports the actual Wafeq/
    // provider sync outcome once the fan-out settles, so a batch that
    // approved cleanly but failed to sync a few invoices doesn't read as a
    // full success.
    const unsubBulkSyncCompleted = sse.on("invoice_bulk_sync_completed", (data) => {
      const d = data as { payload?: Record<string, unknown>; } & Record<string, unknown>;
      const payload = (d.payload || d) as Record<string, unknown>;
      // OCT-16164: only the user who ran the batch gets its toast; every
      // client still refreshes its queries below.
      const isOwnBatch = isOwnBulkEvent(payload, currentUserIdRef.current);
      const synced = (payload.synced as number) ?? 0;
      const failed = (payload.failed as number) ?? 0;

      if (isOwnBatch) {
        toast({
          title: failed === 0 ? "Bulk sync complete" : "Some invoices failed to sync",
          description: failed === 0
            ? `${synced} invoice${synced === 1 ? "" : "s"} synced to the accounting system.`
            : `${synced} of ${synced + failed} approved invoices synced (${failed} failed - review sync status).`,
          variant: failed === 0 ? "default" : "destructive",
          duration: 8000,
        });
      }

      queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
      queryClient.invalidateQueries({ queryKey: ["/api/invoices"] });
    });

    const unsubAll = sse.on("*", (_data) => {
      if (wildcardInvalidateTimer) clearTimeout(wildcardInvalidateTimer);
      wildcardInvalidateTimer = setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
      }, 3000);
    });

    sse.connect();

    return () => {
      if (wildcardInvalidateTimer) clearTimeout(wildcardInvalidateTimer);
      unsubPayment();
      unsubDisputeOpened();
      unsubDisputeResolved();
      unsubDisputeStatusChanged();
      unsubBulkImportStarted();
      unsubBulkImportCompleted();
      unsubBulkApproveStarted();
      unsubBulkApproveCompleted();
      unsubBulkSyncCompleted();
      unsubAll();
      dismissConnectionToast?.();
    };
  }, [toast, playPaymentSound, t]);

  const unreadCount = notificationsData?.unreadCount ?? 0;
  const notifications = notificationsData?.data ?? [];
  const filteredNotifications = categoryFilter === "all"
    ? notifications
    : notifications.filter((n) => n.category === categoryFilter);
  const newestUnreadTs = notifications
    .filter((n) => !n.isRead && n.createdAt)
    .reduce<string | null>((max, n) => {
      const ts = String(n.createdAt);
      if (!max) return ts;
      return new Date(ts).getTime() > new Date(max).getTime() ? ts : max;
    }, null);
  const badgeUrgency = getNotificationBadgeUrgency(unreadCount, newestUnreadTs);

  const markReadMutation = useMutation({
    mutationFn: (id: string) => apiRequest("PATCH", `/api/notifications/${id}/read`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
    },
    onError: (err: Error) => {
      toast({
        title: t("notifications.markReadFailed", { defaultValue: "Failed to mark notification as read" }),
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const markAllReadMutation = useMutation({
    mutationFn: () => apiRequest("PATCH", "/api/notifications/read-all"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
    },
    onError: (err: Error) => {
      toast({
        title: t("notifications.markAllReadFailed", { defaultValue: "Failed to mark all notifications as read" }),
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const snoozeMutation = useMutation({
    mutationFn: ({ id, duration }: { id: string; duration: string; }) =>
      apiRequest("PATCH", `/api/notifications/${id}/snooze`, { duration }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
      setSnoozeMenuId(null);
      toast({ title: t("notifications.snoozed", "Notification snoozed") });
    },
    onError: (err: Error) => {
      toast({
        title: t("notifications.snoozeFailed", { defaultValue: "Failed to snooze notification" }),
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.isRead) {
      markReadMutation.mutate(notification.id);
    }
    const href = notificationHref(notification);
    if (href) {
      setLocation(href);
    }
    setOpen(false);
  };

  function getCategoryIcon(notification: Notification) {
    const sev = notification.severity;
    const cat = notification.category;
    if (sev === "critical" || sev === "error") return <XCircle className="w-4 h-4 mt-0.5 shrink-0 text-destructive" />;
    if (sev === "warning") return <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-500" />;
    if (cat === "sync") return <RefreshCw className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground" />;
    if (cat === "payment") return <CreditCard className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground" />;
    if (cat === "approval") return <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground" />;
    if (cat === "email") return <Mail className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground" />;
    if (cat === "integration") return <Link2 className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground" />;
    if (cat === "inbound") return <InboxIcon className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground" />;
    if (notification.resourceType === "automation") {
      return <Zap className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground" />;
    }
    if (sev === "info" && (notification.type === "sync_completed" || notification.type?.includes("sync"))) {
      return <CheckCircle className="w-4 h-4 mt-0.5 shrink-0 text-emerald-500" />;
    }
    return <FileText className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground" />;
  }

  function getCategoryLabel(notification: Notification): string | null {
    const cat = notification.category;
    if (cat === "sync") return "Sync";
    if (cat === "payment") return "Payment";
    if (cat === "approval") return "Approval";
    if (cat === "email") return "Email";
    if (cat === "integration") return "Integration";
    if (cat === "inbound") return "Inbound";
    return null;
  }

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            className="relative p-1.5 rounded-md text-muted-foreground hover:text-foreground transition-all hover-elevate min-w-[44px] min-h-[44px] md:min-w-0 md:min-h-0 flex items-center justify-center"
            data-testid="button-notifications"
            aria-label={tAp("notifications.title")}
          >
            <Bell className="w-4 h-4" />
            {unreadCount > 0 && (
              <span
                className={`absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full ${badgeUrgency.bg} ${badgeUrgency.text} text-[10px] font-mono font-semibold leading-none ${
                  badgeUrgency.animate ? "animate-pulse" : ""
                }`}
                data-testid="badge-notification-count"
              >
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-[calc(100vw-2rem)] sm:w-96 p-0" data-testid="panel-notifications">
          <div className="flex items-center justify-between gap-2 px-4 py-3 border-b">
            <span className="text-sm font-semibold" data-testid="text-notifications-title">
              {tAp("notifications.recentActivity")}
            </span>
            {unreadCount > 0 && (
              <button
                onClick={() => markAllReadMutation.mutate()}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                data-testid="button-mark-all-read"
                disabled={markAllReadMutation.isPending}
              >
                <CheckCheck className="w-3 h-3" />
                {tAp("notifications.markAllRead")}
              </button>
            )}
          </div>
          <div className="flex items-center gap-1 px-3 py-2 border-b overflow-x-auto">
            {(["all", "sync", "approval", "inbound", "email", "integration", "payment"] as const).map((cat) => {
              const count = cat === "all"
                ? notifications.length
                : notifications.filter((n) => n.category === cat).length;
              if (cat !== "all" && count === 0) return null;
              return (
                <button
                  key={cat}
                  onClick={() => setCategoryFilter(cat)}
                  className={`px-2 py-1 rounded text-[10px] font-mono uppercase tracking-wider whitespace-nowrap transition-colors ${
                    categoryFilter === cat
                      ? "bg-foreground text-background"
                      : "bg-muted text-muted-foreground hover:text-foreground"
                  }`}
                  data-testid={`filter-category-${cat}`}
                >
                  {cat === "all" ? "All" : cat} {count > 0 ? `(${count})` : ""}
                </button>
              );
            })}
          </div>
          <div className="max-h-[60vh] overflow-y-auto">
            {filteredNotifications.length === 0
              ? (
                <div
                  className="px-4 py-8 text-center text-sm text-muted-foreground"
                  data-testid="text-notifications-empty"
                >
                  {tAp("notifications.empty")}
                </div>
              )
              : (
                <div className="py-1">
                  {filteredNotifications.map((notification) => {
                    const itemUrgency = !notification.isRead && notification.createdAt
                      ? getFailureRecencyUrgency(String(notification.createdAt))
                      : 1;
                    const itemCls = URGENCY_CLASSES[itemUrgency as 1 | 2 | 3 | 4 | 5];
                    return (
                      <div
                        key={notification.id}
                        className={`w-full text-left px-4 py-3 min-h-[44px] transition-colors flex gap-3 items-start ${
                          !notification.isRead ? `border-l-2 ${itemCls.border}` : "border-l-2 border-l-transparent"
                        }`}
                        data-testid={`notification-item-${notification.id}`}
                      >
                        <button
                          onClick={() => handleNotificationClick(notification)}
                          className="flex gap-3 items-start flex-1 min-w-0 text-left hover-elevate"
                        >
                          {getCategoryIcon(notification)}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              <p
                                className={`text-sm leading-snug break-words ${
                                  !notification.isRead ? "font-semibold" : ""
                                }`}
                                data-testid={`text-notification-title-${notification.id}`}
                              >
                                {notification.title}
                              </p>
                            </div>
                            {notification.message && (
                              <p
                                className="text-xs text-muted-foreground leading-snug mt-0.5"
                                data-testid={`text-notification-message-${notification.id}`}
                              >
                                {parsePushFailureMessage(notification.message)}
                              </p>
                            )}
                            <div className="flex items-center gap-2 mt-1">
                              {getCategoryLabel(notification) && (
                                <span
                                  className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground/70 bg-muted px-1.5 py-0.5 rounded"
                                  data-testid={`badge-notification-category-${notification.id}`}
                                >
                                  {getCategoryLabel(notification)}
                                </span>
                              )}
                              <span
                                className="text-[10px] font-mono text-muted-foreground"
                                data-testid={`text-notification-time-${notification.id}`}
                              >
                                {notification.createdAt ? formatRelativeTime(String(notification.createdAt)) : ""}
                              </span>
                            </div>
                          </div>
                        </button>
                        <div className="flex items-center gap-1 shrink-0 mt-0.5">
                          <div className="relative">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSnoozeMenuId(snoozeMenuId === notification.id ? null : notification.id);
                              }}
                              className="p-1 rounded hover:bg-muted transition-colors"
                              title={t("notifications.snooze", "Snooze")}
                              data-testid={`button-snooze-${notification.id}`}
                            >
                              <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                            </button>
                            {snoozeMenuId === notification.id && (
                              <div
                                className="absolute right-0 top-7 z-50 bg-popover border rounded-md shadow-md py-1 min-w-[140px]"
                                data-testid={`menu-snooze-${notification.id}`}
                              >
                                {[
                                  { key: "1h", label: t("notifications.snooze1h", "1 hour") },
                                  { key: "3h", label: t("notifications.snooze3h", "3 hours") },
                                  { key: "tomorrow", label: t("notifications.snoozeTomorrow", "Tomorrow") },
                                  { key: "next_week", label: t("notifications.snoozeNextWeek", "Next week") },
                                ].map((opt) => (
                                  <button
                                    key={opt.key}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      snoozeMutation.mutate({ id: notification.id, duration: opt.key });
                                    }}
                                    className="w-full text-left px-3 py-1.5 text-xs hover:bg-muted transition-colors"
                                    data-testid={`snooze-option-${opt.key}-${notification.id}`}
                                  >
                                    {opt.label}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                          {/* OCT-16925 / ILLEGAL E157 - Viewers get no quick-task trigger. */}
                          {canWrite && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setTaskDialogNotification(notification);
                                setOpen(false);
                              }}
                              className="p-1 rounded hover:bg-muted transition-colors"
                              title={t("notifications.createTask", "Create Task")}
                              data-testid={`button-create-task-${notification.id}`}
                            >
                              <ListTodo className="w-3.5 h-3.5 text-muted-foreground" />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
          </div>
        </PopoverContent>
      </Popover>

      <QuickTaskDialog
        open={!!taskDialogNotification}
        onOpenChange={(v) => {
          if (!v) setTaskDialogNotification(null);
        }}
        defaultName={taskDialogNotification?.title || ""}
        defaultNotes={taskDialogNotification?.message || ""}
        {...(taskDialogNotification?.resourceType ? { defaultSourceType: taskDialogNotification.resourceType } : {})}
        {...(taskDialogNotification?.resourceId ? { defaultSourceId: taskDialogNotification.resourceId } : {})}
      />
    </>
  );
}

export function HeaderNav() {
  const [location, setLocation] = useLocation();
  const { user, logout } = useAuth();
  // OCT-3366: switcher consumes `visibleEntities` (active by default,
  // archived added when the operator toggles "Show archived" on). The
  // dropdown also exposes that toggle so an admin can briefly reveal an
  // archived entity to read its history; selecting an archived entity is
  // allowed but writes are blocked by the active-entity middleware (E90),
  // and `useRollupWriteGuard` mirrors that block in every entity-scoped
  // form on the client side.
  const { entities, visibleEntities, activeEntity, activeEntityId, isAllEntities, showArchived, setShowArchived } =
    useActiveEntity();
  const switchEntity = useEntitySwitch();
  const { t } = useTranslation("common");
  const features = useFeatures();
  const [entityTransition, setEntityTransition] = useState(false);

  // OCT-10684 / DOMAIN D23: the count's automation slice is entity-scoped
  // server-side, so the entity belongs in the key or a switch renders the
  // previous entity's badge until the refetch lands. The explicit `queryFn` is
  // required: the default one joins key elements into the URL, which would turn
  // the entity segment into a bogus path.
  const { data: inboxCountData } = useQuery<
    { count: number; billCount: number; invoiceCount: number; automationCount: number; }
  >({
    queryKey: ["/api/inbox/count", isAllEntities ? "all" : activeEntityId],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/inbox/count");
      if (!res.ok) throw new Error("Failed to fetch inbox count");
      return res.json();
    },
    refetchInterval: 60000,
    staleTime: 30000,
  });
  const pendingApprovalsCount = inboxCountData?.count ?? 0;

  const { hasNew, markSeen, totalNewCount } = useFeatureAwareness();

  const filterByFeature = (items: NavItem[]) =>
    items.filter((item) => {
      if (!item.featureKey) return true;
      if (features[item.featureKey] === false) return false;
      if (item.featureKey === "financing" && !isFinancingAvailable(features)) return false;
      return true;
    });

  const { active: inSupportMode } = useSupportMode();
  const isSuperAdmin = user?.role === "superadmin" && !inSupportMode;

  const primaryItems = filterByFeature(PRIMARY_NAV_ITEMS);

  const filteredPrimary = isSuperAdmin ? [] : primaryItems;

  const isActive = (url: string) => {
    if (url === "/") return location === "/";
    return location.startsWith(url);
  };

  const handleEntitySwitch = (id: string) => {
    if (id === activeEntity?.id && !isAllEntities) return;
    if (id === "all" && isAllEntities) return;
    setEntityTransition(true);
    // OCT-4558: stay on the current page. `setActiveEntityId` already
    // invalidates entity-scoped TanStack Query keys (see
    // client/src/lib/entity-context.tsx), so the current page's data
    // refetches for the newly selected entity. Do not navigate home -
    // that discarded report paths, filters, and other page context.
    //
    // OCT-10356: because the page does not move, `switchEntity` raises the
    // confirmation toast naming the entity now active - the fade overlay and
    // the switcher's own label are too easy to miss on their own.
    switchEntity(id);
    setTimeout(() => setEntityTransition(false), 300);
  };

  return (
    <>
      {entityTransition && (
        <div className="fixed inset-0 bg-background/60 z-[100] pointer-events-none animate-in fade-in duration-200" />
      )}
      <header className="px-4 sm:px-6 py-3 shrink-0 overflow-x-hidden max-w-full">
        <div className="flex items-center justify-between gap-2 sm:gap-4 min-w-0">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0 flex-1">
            <Link
              href={isSuperAdmin ? "/admin" : "/"}
              className="flex items-center gap-2 sm:gap-2.5 shrink-0"
              data-testid="link-home"
            >
              <OctaLogo />
              <div className="flex items-baseline gap-1 sm:gap-1.5">
                <span className="text-sm font-semibold" style={{ letterSpacing: "0.15em" }}>OCTA</span>
                <span
                  className="text-xs font-mono text-muted-foreground hidden sm:inline"
                  style={{ letterSpacing: "0.25em" }}
                >
                  CORE
                </span>
              </div>
            </Link>

            {!isSuperAdmin && entities.length === 1 && (
              <>
                <span className="text-muted-foreground/20 select-none hidden md:inline">|</span>
                <div
                  className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1.5 text-xs sm:text-sm max-w-[120px] sm:max-w-[220px] min-w-0"
                  data-testid="text-single-entity"
                >
                  <div className="w-1.5 h-1.5 rounded-full bg-foreground shrink-0" />
                  <span className="truncate font-medium">{entities[0]?.name}</span>
                  {entities[0]?.currency && (
                    <span className="text-[10px] font-mono text-muted-foreground shrink-0">{entities[0].currency}</span>
                  )}
                </div>
              </>
            )}
            {!isSuperAdmin && entities.length > 1 && (
              <>
                <span className="text-muted-foreground/20 select-none hidden md:inline">|</span>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1.5 rounded-md border border-border/60 hover:bg-muted transition-colors text-xs sm:text-sm max-w-[160px] sm:max-w-[220px] min-w-0 min-h-[44px] sm:min-h-0"
                      data-testid="button-entity-switcher"
                    >
                      <div className="w-1.5 h-1.5 rounded-full bg-foreground shrink-0" />
                      <span className="truncate font-medium">
                        {isAllEntities ? t("nav.allEntities") : (activeEntity?.name || t("nav.selectEntity"))}
                      </span>
                      {!isAllEntities && activeEntity?.currency && (
                        <span className="text-[10px] font-mono text-muted-foreground shrink-0 hidden sm:inline">
                          {activeEntity.currency}
                        </span>
                      )}
                      <ChevronDown className="h-3 w-3 text-muted-foreground shrink-0" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    align="start"
                    className="w-[calc(100vw-2rem)] sm:w-56 max-h-[70vh] overflow-y-auto"
                    data-testid="menu-entity-list"
                  >
                    <DropdownMenuItem
                      onClick={() => handleEntitySwitch("all")}
                      className="flex items-center justify-between min-h-[44px] md:min-h-0"
                      data-testid="menu-item-entity-all"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <Layers className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                        <span className="truncate font-medium">{t("nav.allEntities")}</span>
                      </div>
                      {isAllEntities && <Check className="h-3.5 w-3.5 shrink-0" />}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    {
                      /* OCT-3362 + OCT-3366: archived entities are hidden
                        from the switcher by default - they cannot be the
                        resting selection because the active-entity
                        middleware refuses to resolve archived ids (E90).
                        The "Show archived" toggle below reveals them so
                        an admin can briefly drop into a read-only view
                        of an archived entity's data; once revealed the
                        archived row is labelled and tagged with an
                        Archived chip so it never reads as live. */
                    }
                    {visibleEntities.map((entity) => (
                      <DropdownMenuItem
                        key={entity.id}
                        onClick={() => handleEntitySwitch(entity.id)}
                        className="flex items-center justify-between min-h-[44px] md:min-h-0"
                        data-testid={`menu-item-entity-${entity.id}`}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <Building2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                          <span className={`truncate ${entity.isArchived ? "text-muted-foreground" : ""}`}>
                            {entity.name}
                          </span>
                          {entity.isDefault && (
                            <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                              {t("nav.default")}
                            </span>
                          )}
                          {entity.isArchived && (
                            <span
                              className="text-[10px] font-mono uppercase tracking-widest text-amber-600 dark:text-amber-400"
                              data-testid={`badge-archived-${entity.id}`}
                            >
                              {t("nav.archived", { defaultValue: "Archived" })}
                            </span>
                          )}
                        </div>
                        {!isAllEntities && entity.id === activeEntity?.id && <Check className="h-3.5 w-3.5 shrink-0" />}
                      </DropdownMenuItem>
                    ))}
                    {entities.some((e) => e.isArchived) && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onSelect={(e) => {
                            // Keep dropdown open after toggling so the operator can
                            // see the archived rows appear in place.
                            e.preventDefault();
                            setShowArchived(!showArchived);
                          }}
                          className="flex items-center justify-between min-h-[44px] md:min-h-0"
                          data-testid="toggle-show-archived"
                        >
                          <span className="text-xs text-muted-foreground">
                            {t("nav.showArchived", { defaultValue: "Show archived" })}
                          </span>
                          {showArchived && <Check className="h-3.5 w-3.5 shrink-0" />}
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => setLocation("/settings?section=organization&sub=units")}
                      className="min-h-[44px] md:min-h-0"
                      data-testid="menu-item-manage-entities"
                    >
                      <Plus className="h-3.5 w-3.5 mr-2" />
                      {t("nav.manageEntities")}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}
          </div>

          {!isSuperAdmin && (
            <div className="md:hidden flex items-center gap-0.5">
              <button
                onClick={() => {
                  document.dispatchEvent(new KeyboardEvent("keydown", { key: "k", metaKey: true }));
                }}
                className="min-w-[44px] min-h-[44px] flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground transition-all"
                data-testid="button-mobile-search"
                aria-label={t("cmd.search")}
              >
                <Search className="w-4 h-4" />
              </button>
              <NotificationBell />
            </div>
          )}

          {isSuperAdmin && (
            <div className="md:hidden flex items-center gap-1">
              <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                {user?.email}
              </span>
            </div>
          )}

          <div className="hidden md:flex items-center gap-2">
            {!isSuperAdmin && <CommandPaletteTrigger />}

            <nav className="flex items-center gap-1">
              {filteredPrimary.map((item) => {
                const active = isActive(item.url);
                const muted = isAllEntities && item.entityScoped;
                const labeled = item.navKey === "ar" || item.navKey === "ap";
                const linkEl = (
                  <Link
                    href={item.url}
                    onMouseEnter={() => prefetchRoute(item.url)}
                    onFocus={() => prefetchRoute(item.url)}
                    className={`relative ${
                      labeled ? "inline-flex items-center gap-1.5 px-2 py-1.5" : "p-1.5"
                    } rounded-md transition-all hover-elevate ${
                      active ? "text-foreground" : "text-muted-foreground hover:text-foreground"
                    } ${muted ? "opacity-50" : ""}`}
                    data-testid={item.testId}
                  >
                    <item.icon className="w-4 h-4" />
                    {labeled && <span className="text-sm font-medium whitespace-nowrap">{t(item.title)}</span>}
                    {active && (
                      <span className="absolute -bottom-[13px] left-1/2 -translate-x-1/2 w-4 h-px bg-foreground" />
                    )}
                  </Link>
                );
                if (labeled) {
                  return <div key={item.url}>{linkEl}</div>;
                }
                return (
                  <Tooltip key={item.url}>
                    <TooltipTrigger asChild>
                      {linkEl}
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="text-xs">
                      {t(item.title)}
                      {muted ? ` (${t("nav.readOnly", "read-only")})` : ""}
                    </TooltipContent>
                  </Tooltip>
                );
              })}
            </nav>

            {!isSuperAdmin && <ConfidenceIndicator />}
            {!isSuperAdmin && <NotificationBell />}

            {!isSuperAdmin && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    onMouseEnter={() => {
                      for (
                        const r of [
                          "/inbox",
                          "/automations",
                          "/reports",
                          "/chat",
                          "/tasks",
                          "/communications",
                          "/settings",
                        ]
                      ) {
                        prefetchRoute(r);
                      }
                    }}
                    className="relative p-1.5 rounded-md text-muted-foreground hover:text-foreground transition-all hover-elevate"
                    data-testid="button-more-menu"
                    aria-label={t("nav.more")}
                  >
                    <MoreHorizontal className="w-4 h-4" />
                    {totalNewCount > 0 && (
                      <span
                        className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-primary animate-pulse"
                        data-testid="badge-new-features-indicator"
                      />
                    )}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48" data-testid="menu-more">
                  <DropdownMenuItem onClick={() => setLocation("/inbox")} data-testid="menu-item-inbox">
                    <InboxIcon className="w-4 h-4 mr-2" />
                    <span className="flex-1">{t("nav.inbox")}</span>
                    {pendingApprovalsCount > 0 && (
                      <span
                        className="ml-auto flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-mono font-semibold leading-none"
                        data-testid="badge-pending-approvals"
                      >
                        {pendingApprovalsCount > 99 ? "99+" : pendingApprovalsCount}
                      </span>
                    )}
                  </DropdownMenuItem>
                  {features.automations !== false && (
                    <DropdownMenuItem
                      onClick={() => {
                        markSeen("automations");
                        setLocation("/automations");
                      }}
                      data-testid="menu-item-automations"
                    >
                      <Zap className="w-4 h-4 mr-2" />
                      <span className="flex-1">{t("nav.automations")}</span>
                      {hasNew("automations") && (
                        <span
                          className="ml-auto w-2 h-2 rounded-full bg-primary animate-pulse"
                          data-testid="badge-new-automations"
                        />
                      )}
                    </DropdownMenuItem>
                  )}
                  {features.aiReports !== false && (
                    <DropdownMenuItem
                      onClick={() => {
                        markSeen("aiReports");
                        setLocation("/reports");
                      }}
                      data-testid="menu-item-reports"
                    >
                      <BarChart3 className="w-4 h-4 mr-2" />
                      <span className="flex-1">{t("nav.reports")}</span>
                      {hasNew("aiReports") && (
                        <span
                          className="ml-auto w-2 h-2 rounded-full bg-primary animate-pulse"
                          data-testid="badge-new-reports"
                        />
                      )}
                    </DropdownMenuItem>
                  )}
                  {features.aiChat !== false && (
                    <DropdownMenuItem onClick={() => setLocation("/chat")} data-testid="menu-item-chat">
                      <MessageSquare className="w-4 h-4 mr-2" />
                      {t("nav.chat")}
                    </DropdownMenuItem>
                  )}
                  {features.tasks !== false && (
                    <DropdownMenuItem onClick={() => setLocation("/tasks")} data-testid="menu-item-tasks">
                      <ClipboardList className="w-4 h-4 mr-2" />
                      {t("nav.tasks")}
                    </DropdownMenuItem>
                  )}
                  {features.communications !== false && (
                    <DropdownMenuItem
                      onClick={() => {
                        markSeen("communications");
                        setLocation("/communications");
                      }}
                      data-testid="menu-item-communications"
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      <span className="flex-1">{t("nav.communications")}</span>
                      {hasNew("communications") && (
                        <span
                          className="ml-auto w-2 h-2 rounded-full bg-primary animate-pulse"
                          data-testid="badge-new-communications"
                        />
                      )}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setLocation("/system-health")} data-testid="menu-item-system-health">
                    <ActivitySquare className="w-4 h-4 mr-2" />
                    System Health
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setLocation("/settings")} data-testid="menu-item-settings">
                    <Settings className="w-4 h-4 mr-2" />
                    {t("nav.settings")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {isSuperAdmin && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Link
                    href="/admin"
                    className={`relative p-1.5 rounded-md transition-all hover-elevate ${
                      isActive("/admin") ? "text-foreground" : "text-muted-foreground hover:text-foreground"
                    }`}
                    data-testid="link-admin"
                  >
                    <Shield className="w-4 h-4" />
                    {isActive("/admin") && (
                      <span className="absolute -bottom-[13px] left-1/2 -translate-x-1/2 w-4 h-px bg-foreground" />
                    )}
                  </Link>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="text-xs">
                  Admin
                </TooltipContent>
              </Tooltip>
            )}

            <span className="text-muted-foreground/20 mx-0.5 select-none">|</span>

            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={logout}
                  className="p-1.5 rounded-md text-muted-foreground hover:text-foreground transition-all hover-elevate"
                  data-testid="button-logout"
                  aria-label={t("nav.logout")}
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="text-xs">
                {t("nav.logout")}
              </TooltipContent>
            </Tooltip>
          </div>
        </div>
      </header>
    </>
  );
}
